import {
    allocationCountForRequirement,
    buildRequirementCategorySummaryStrings,
    buildRequirementSummaries,
    buildShortageSummaryStrings,
    isRequirementSatisfied,
    needsDriverNotification,
    notifiesOnlyDrivers,
    pendingDriverNotificationCount,
} from './allocation'
import {allocationStatusForRow, sortJobsWithinColumn} from './attention-lens'
import type {DayBarPlacement} from './day-board-layout'
import type {TimelineJobRow} from './job-rows'
import {resolveJobRequirements} from './job-rows'
import {scheduleKindLabel, timedJobStartLabel} from './job-schedule'
import type {AllocationStatus} from './types'
import {SLOTS_PER_DAY} from './work-day'

const COMMITTED_BAR_FILL = 'var(--mantine-color-gray-1)'

export const ATTENTION_CARD_BORDER = '1px solid var(--mt-signal-attention-3)'

const ATTENTION_STYLES = {
    backgroundColor: 'var(--mt-signal-attention-2)',
    border: ATTENTION_CARD_BORDER,
    accentColor: 'var(--mt-signal-attention-8)',
}

export const ATTENTION_ROW_INSET_Y = 6

/** Minimum committed row height — rows grow with content. */
export const COMMITTED_ROW_MIN_HEIGHT = 32

/** Needs-attention section shares the board surface; cards carry the warm fill. */
export const ATTENTION_PANEL_SURFACE = 'transparent'

/** Quieter section behind committed rows — same canvas, not a separate layer. */
export const COMMITTED_PANEL_SURFACE = 'var(--mantine-color-white)'
export const COMMITTED_PANEL_BAND = 'var(--mantine-color-gray-0)'
export const COMMITTED_PANEL_BORDER = 'var(--mantine-color-gray-2)'

export const SECTION_DIVIDER = '1px solid var(--mantine-color-gray-2)'

export type CommitmentJobDisplay = {
    title: string
    jobCode: string
    customer: string
    assignedNames: string[]
    assignedDriverCount: number
    unnotifiedDriverCount: number
    unnotifiedOnlyDrivers: boolean
    missingLabels: string[]
    /** Category-level shortages for terminal board rows. */
    shortageSummaryStrings: string[]
    /** Total crew/equipment counts for board identity lines. */
    requirementCategorySummaryLine: string
    requirementBadges: string[]
    requirementSummaryLine: string
    durationLabel: string
    timedLabel?: string
    needsAttention: boolean
    allocationStatus: AllocationStatus
    showAllocate: boolean
    spansMultipleDays: boolean
}

export type CommitmentBarTier = 'attention' | 'committed'

export type CommitmentSurfaceStyles = {
    tier: CommitmentBarTier
    backgroundColor: string
    border?: string
    accentColor?: string
}

export type CommitmentBarLayout = {
    tier: CommitmentBarTier
}

/** Shortage-first label for a single unmet requirement gap. */
export function formatShortageEntry(label: string, gap: number, quantity: number): string {
    const normalized = label.trim()
    if (gap === quantity && quantity === 1) {
        return singularizeResourceLabel(normalized)
    }
    if (gap === 1) {
        return `1 ${singularizeResourceLabel(normalized)}`
    }
    return `${gap} ${normalized}`
}

function singularizeResourceLabel(label: string): string {
    if (label === 'Drivers') {
        return 'Driver'
    }
    if (label.endsWith('s')) {
        return label.slice(0, -1)
    }
    return label
}

export function buildCommitmentJobDisplay(job: TimelineJobRow): CommitmentJobDisplay {
    const needsAttention = job.status === 'unassigned'
    const allocationStatus = allocationStatusForRow(job)
    const source = job.unassignedJob

    const assignedNames = job.allocations.map((entry) => entry.resourceName)
    const assignedDriverCount = job.allocations.filter((entry) => entry.kind === 'worker').length
    const unnotifiedDriverCount = source
        ? pendingDriverNotificationCount(source)
        : job.allocations.filter(needsDriverNotification).length
    const requirements = resolveJobRequirements(job)
    const unnotifiedOnlyDrivers = notifiesOnlyDrivers(
        source?.allocations ?? job.allocations,
        source?.requirements ?? requirements,
    )
    const missingLabels: string[] = []
    let shortageSummaryStrings: string[] = []
    const requirementCategorySummaryLine =
        requirements.length > 0
            ? buildRequirementCategorySummaryStrings(requirements).join(' · ')
            : ''
    const requirementBadges: string[] = []

    if (source) {
        shortageSummaryStrings = buildShortageSummaryStrings(source)
        requirementBadges.push(...buildRequirementSummaries(source))
        for (const requirement of source.requirements) {
            const count = allocationCountForRequirement(source, requirement.id)
            if (!isRequirementSatisfied(source, requirement)) {
                const gap = requirement.quantity - count
                missingLabels.push(
                    formatShortageEntry(requirement.label, gap, requirement.quantity),
                )
            }
        }
    }

    let durationLabel = scheduleKindLabel(job.scheduleKind)
    let timedLabel: string | undefined
    if (job.scheduleKind === 'timed' && job.span) {
        const slotInDay = job.span.startSlotIndex % SLOTS_PER_DAY
        timedLabel = timedJobStartLabel({...job.span, startSlotIndex: slotInDay})
        durationLabel = timedLabel
    }

    return {
        title: job.label,
        jobCode: job.jobCode,
        customer: job.customer,
        assignedNames,
        assignedDriverCount,
        unnotifiedDriverCount,
        unnotifiedOnlyDrivers,
        missingLabels,
        shortageSummaryStrings,
        requirementCategorySummaryLine,
        requirementBadges,
        requirementSummaryLine: requirementBadges.join(' · '),
        durationLabel,
        timedLabel,
        needsAttention,
        allocationStatus,
        showAllocate: needsAttention && source != null,
        spansMultipleDays: job.scheduleKind === 'two_day',
    }
}

export function surfaceStylesForJob(display: CommitmentJobDisplay): CommitmentSurfaceStyles {
    if (!display.needsAttention) {
        return {
            tier: 'committed',
            backgroundColor: COMMITTED_BAR_FILL,
        }
    }

    return {
        tier: 'attention',
        ...ATTENTION_STYLES,
    }
}

export function barLayoutForDisplay(display: CommitmentJobDisplay): CommitmentBarLayout {
    return {tier: surfaceStylesForJob(display).tier}
}

export function committedMetaLine(display: CommitmentJobDisplay): string | undefined {
    if (display.requirementCategorySummaryLine) {
        return display.requirementCategorySummaryLine
    }
    return display.customer
}

export function groupJobsIntoDayColumns(
    jobs: TimelineJobRow[],
    placementFor: (job: TimelineJobRow) => DayBarPlacement,
    visibleDayCount: number,
): TimelineJobRow[][] {
    const byDay: TimelineJobRow[][] = Array.from({length: visibleDayCount}, () => [])

    for (const job of jobs) {
        const placement = placementFor(job)
        if (placement.kind === 'day_span' && placement.startDayIndex < visibleDayCount) {
            byDay[placement.startDayIndex]?.push(job)
        }
    }

    return byDay.map((column) => sortJobsWithinColumn(column))
}

export function continuationSuffix(spansMultipleDays: boolean): string {
    return spansMultipleDays ? ' →' : ''
}

/** Span hint when a multi-day job is shown in a single day column (e.g. 1-day view). */
export function attentionContinuationLabel(
    display: CommitmentJobDisplay,
    placement: DayBarPlacement | null,
): string | undefined {
    if (!display.spansMultipleDays || !placement || placement.kind !== 'day_span') {
        return undefined
    }

    if (placement.daySpan > 1) {
        return undefined
    }

    return 'Continues tomorrow'
}

/** Left-to-right subtext: optional time, requirements, optional continuation. */
export function attentionSubtextLine(
    display: CommitmentJobDisplay,
    placement: DayBarPlacement | null,
): string | undefined {
    const parts: string[] = []

    if (display.timedLabel) {
        parts.push(display.timedLabel)
    }

    if (display.requirementSummaryLine) {
        parts.push(display.requirementSummaryLine)
    }

    const continuation = attentionContinuationLabel(display, placement)
    if (continuation) {
        parts.push(continuation)
    }

    return parts.length > 0 ? parts.join(' · ') : undefined
}

export function attentionTitleSuffix(
    display: CommitmentJobDisplay,
    placement: DayBarPlacement,
): string {
    if (!display.spansMultipleDays || placement.kind !== 'day_span' || placement.daySpan <= 1) {
        return ''
    }

    return continuationSuffix(true)
}
